/**
 * TopPerformersTable Component
 * 
 * Sortable table showing top performing ZIP codes with pagination,
 * search functionality, and CSV export capabilities
 */

import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Form, 
  Button, 
  Badge, 
  Pagination, 
  InputGroup,
  ButtonGroup,
  Dropdown
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrophy,
  faSearch,
  faDownload,
  faSort,
  faSortUp,
  faSortDown,
  faFilter,
  faMapMarkerAlt,
  faEye,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

// Import types
import type { 
  TopPerformersTableProps,
  TopPerformer,
  PerformanceMetric,
  SortOrder,
  MarketTemperature
} from '../../../../types/marketIntelligence.types';

// Market temperature colors
const TEMPERATURE_COLORS: Record<MarketTemperature, string> = {
  'Hot Seller Market': '#ef4444',
  'Strong Market': '#f59e0b',
  'Balanced Market': '#10b981',
  'Buyer Market': '#3b82f6',
  'Cold Market': '#6b7280'
};

// Market temperature emojis
const TEMPERATURE_EMOJIS: Record<MarketTemperature, string> = {
  'Hot Seller Market': '🔥',
  'Strong Market': '📈',
  'Balanced Market': '⚖️',
  'Buyer Market': '📉',
  'Cold Market': '❄️'
};

// Mock data generator
const generateMockPerformers = (count: number = 50): TopPerformer[] => {
  const performers: TopPerformer[] = [];
  const temperatures: MarketTemperature[] = [
    'Hot Seller Market',
    'Strong Market', 
    'Balanced Market',
    'Buyer Market',
    'Cold Market'
  ];
  
  const cities = [
    'Phoenix', 'Scottsdale', 'Mesa', 'Chandler', 'Glendale', 
    'Tempe', 'Peoria', 'Surprise', 'Gilbert', 'Tucson',
    'Cave Creek', 'Paradise Valley', 'Avondale', 'Goodyear'
  ];

  for (let i = 0; i < count; i++) {
    const zipCode = `${85000 + Math.floor(Math.random() * 400)}`;
    const city = cities[Math.floor(Math.random() * cities.length)];
    const temperature = temperatures[Math.floor(Math.random() * temperatures.length)];
    const mai = Math.random() * 100;
    const price = 150000 + Math.random() * 400000;
    
    performers.push({
      rank: i + 1,
      zip_code: zipCode,
      market_action_index: Math.round(mai * 10) / 10,
      median_close_price: Math.round(price),
      total_volume: Math.round(price * (Math.random() * 10 + 1)),
      daily_sales_count: Math.floor(Math.random() * 20) + 1,
      market_temperature: temperature,
      market_emoji: TEMPERATURE_EMOJIS[temperature],
      analysis_date: new Date().toISOString().split('T')[0],
      metric_value: mai
    });
  }
  
  return performers;
};

// Column configuration
interface Column {
  key: keyof TopPerformer;
  label: string;
  sortable: boolean;
  format?: 'currency' | 'number' | 'percentage' | 'date';
  width?: string;
}

const COLUMNS: Column[] = [
  { key: 'rank', label: 'Rank', sortable: true, width: '80px' },
  { key: 'zip_code', label: 'ZIP Code', sortable: true, width: '120px' },
  { key: 'market_temperature', label: 'Temperature', sortable: true, width: '150px' },
  { key: 'market_action_index', label: 'MAI', sortable: true, format: 'number' },
  { key: 'median_close_price', label: 'Median Price', sortable: true, format: 'currency' },
  { key: 'total_volume', label: 'Volume', sortable: true, format: 'currency' },
  { key: 'daily_sales_count', label: 'Sales', sortable: true, format: 'number' },
  { key: 'analysis_date', label: 'Last Updated', sortable: true, format: 'date', width: '120px' }
];

// Format value based on type
const formatValue = (value: any, format?: string): string => {
  if (value === undefined || value === null) return 'N/A';
  
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    
    case 'percentage':
      return `${value.toFixed(1)}%`;
    
    case 'date':
      return new Date(value).toLocaleDateString();
    
    case 'number':
    default:
      return typeof value === 'number' 
        ? new Intl.NumberFormat('en-US').format(value)
        : String(value);
  }
};

// Temperature badge component
const TemperatureBadge: React.FC<{ temperature: MarketTemperature; emoji: string }> = ({ 
  temperature, 
  emoji 
}) => (
  <Badge 
    style={{
      background: `linear-gradient(135deg, ${TEMPERATURE_COLORS[temperature]}20 0%, ${TEMPERATURE_COLORS[temperature]}10 100%)`,
      color: TEMPERATURE_COLORS[temperature],
      border: `1px solid ${TEMPERATURE_COLORS[temperature]}30`,
      borderRadius: '20px',
      padding: '6px 12px',
      fontSize: '0.75rem',
      fontWeight: '500'
    }}
  >
    <span className="me-1">{emoji}</span>
    {temperature.replace(' Market', '')}
  </Badge>
);

// Loading skeleton row
const SkeletonRow: React.FC = () => (
  <tr>
    {COLUMNS.map((col, index) => (
      <td key={index}>
        <div 
          style={{
            height: '20px',
            background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 212, 255, 0.3) 50%, rgba(0, 212, 255, 0.1) 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s infinite',
            borderRadius: '4px'
          }}
        />
      </td>
    ))}
  </tr>
);

const TopPerformersTable: React.FC<TopPerformersTableProps> = ({
  metric = 'mai',
  limit = 20,
  order = 'desc',
  onZipCodeClick,
  showPagination = true,
  showExport = true
}) => {
  const [data, setData] = useState<TopPerformer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof TopPerformer;
    direction: SortOrder;
  }>({ key: 'market_action_index', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(limit);
  const [temperatureFilter, setTemperatureFilter] = useState<MarketTemperature | 'all'>('all');

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // TODO: Replace with actual API call
        // const response = await axios.get(`/api/market-intelligence/top-performers?metric=${metric}&limit=100&order=${order}`);
        // setData(response.data.data.performers);
        
        const mockData = generateMockPerformers(100);
        setData(mockData);
      } catch (err) {
        setError('Failed to load performers data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [metric, order]);

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = data;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.zip_code.includes(searchTerm) ||
        item.market_temperature.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply temperature filter
    if (temperatureFilter !== 'all') {
      filtered = filtered.filter(item => item.market_temperature === temperatureFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
      
      return 0;
    });

    return filtered;
  }, [data, searchTerm, temperatureFilter, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  const paginatedData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle sorting
  const handleSort = (key: keyof TopPerformer) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = COLUMNS.map(col => col.label).join(',');
    const rows = processedData.map(row => 
      COLUMNS.map(col => {
        const value = row[col.key];
        return typeof value === 'string' && value.includes(',') 
          ? `"${value}"` 
          : value;
      }).join(',')
    ).join('\n');
    
    const csvContent = `${headers}\n${rows}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `market-intelligence-performers-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Get sort icon
  const getSortIcon = (key: keyof TopPerformer) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  return (
    <Card 
      className="h-100 border-0"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
        backdropFilter: 'blur(15px)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '16px'
      }}
    >
      <Card.Header 
        className="border-0 bg-transparent"
        style={{ borderRadius: '16px 16px 0 0' }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
          <div className="d-flex align-items-center mb-2 mb-md-0">
            <FontAwesomeIcon 
              icon={faTrophy} 
              style={{ 
                color: '#00d4ff', 
                fontSize: '20px',
                marginRight: '12px',
                filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.3))'
              }} 
            />
            <h6 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>
              Top Performing ZIP Codes
            </h6>
          </div>
          
          {showExport && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={exportToCSV}
              disabled={loading || processedData.length === 0}
              style={{
                borderColor: '#00d4ff',
                color: '#00d4ff'
              }}
            >
              <FontAwesomeIcon icon={faDownload} className="me-1" />
              Export CSV
            </Button>
          )}
        </div>

        {/* Filters and Search */}
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <InputGroup style={{ maxWidth: '300px' }}>
            <InputGroup.Text style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(0, 212, 255, 0.3)' }}>
              <FontAwesomeIcon icon={faSearch} style={{ color: '#6b7280' }} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search ZIP codes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                color: '#1f2937'
              }}
            />
          </InputGroup>

          <Form.Select
            value={temperatureFilter}
            onChange={(e) => setTemperatureFilter(e.target.value as MarketTemperature | 'all')}
            style={{
              width: 'auto',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              color: '#1f2937'
            }}
          >
            <option value="all">All Temperatures</option>
            <option value="Hot Seller Market">🔥 Hot Seller Market</option>
            <option value="Strong Market">📈 Strong Market</option>
            <option value="Balanced Market">⚖️ Balanced Market</option>
            <option value="Buyer Market">📉 Buyer Market</option>
            <option value="Cold Market">❄️ Cold Market</option>
          </Form.Select>

          <Form.Select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            style={{
              width: 'auto',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              color: '#1f2937'
            }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
        </div>
      </Card.Header>

      <Card.Body className="p-0">
        {error ? (
          <div className="text-center p-4">
            <div style={{ color: '#ef4444', marginBottom: '8px' }}>
              Failed to load data
            </div>
            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              {error}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table 
              responsive 
              className="mb-0"
              style={{ fontSize: '0.875rem' }}
            >
              <thead>
                <tr style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
                  {COLUMNS.map((column) => (
                    <th
                      key={String(column.key)}
                      style={{
                        cursor: column.sortable ? 'pointer' : 'default',
                        padding: '12px',
                        borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
                        color: '#1f2937',
                        fontWeight: '600',
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        width: column.width
                      }}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="d-flex align-items-center">
                        {column.label}
                        {column.sortable && (
                          <FontAwesomeIcon
                            icon={getSortIcon(column.key)}
                            className="ms-1"
                            style={{ 
                              fontSize: '0.75rem',
                              opacity: sortConfig.key === column.key ? 1 : 0.3
                            }}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                  <th style={{ width: '100px', textAlign: 'center', borderBottom: '1px solid rgba(0, 212, 255, 0.2)' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonRow key={index} />
                  ))
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="text-center p-4">
                      <div style={{ color: '#6b7280' }}>
                        No data found matching your criteria
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((performer, index) => (
                    <tr 
                      key={performer.zip_code}
                      style={{
                        borderBottom: '1px solid rgba(0, 212, 255, 0.1)',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 212, 255, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td style={{ padding: '12px', fontWeight: '600' }}>
                        #{performer.rank}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon 
                            icon={faMapMarkerAlt} 
                            className="me-2 text-muted" 
                            style={{ fontSize: '12px' }}
                          />
                          {performer.zip_code}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <TemperatureBadge 
                          temperature={performer.market_temperature}
                          emoji={performer.market_emoji}
                        />
                      </td>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#1f2937' }}>
                        {formatValue(performer.market_action_index, 'number')}
                      </td>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#10b981' }}>
                        {formatValue(performer.median_close_price, 'currency')}
                      </td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>
                        {formatValue(performer.total_volume, 'currency')}
                      </td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>
                        {formatValue(performer.daily_sales_count, 'number')}
                      </td>
                      <td style={{ padding: '12px', color: '#6b7280', fontSize: '0.8rem' }}>
                        {formatValue(performer.analysis_date, 'date')}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => onZipCodeClick?.(performer.zip_code)}
                          style={{
                            borderColor: '#00d4ff40',
                            color: '#00d4ff',
                            fontSize: '0.75rem',
                            padding: '4px 8px'
                          }}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>

      {/* Pagination */}
      {showPagination && !loading && processedData.length > itemsPerPage && (
        <Card.Footer 
          className="border-0 bg-transparent"
          style={{ borderRadius: '0 0 16px 16px' }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, processedData.length)} of {processedData.length} entries
            </div>
            
            <Pagination className="mb-0">
              <Pagination.Prev 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              />
              
              {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                const pageNum = currentPage <= 3 
                  ? index + 1 
                  : currentPage >= totalPages - 2 
                    ? totalPages - 4 + index 
                    : currentPage - 2 + index;
                    
                if (pageNum < 1 || pageNum > totalPages) return null;
                
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}
              
              <Pagination.Next 
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              />
            </Pagination>
          </div>
        </Card.Footer>
      )}

      {/* Custom CSS */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </Card>
  );
};

export default TopPerformersTable;